import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { differenceBy } from 'lodash';
import { assignUniqueLayerCode } from '../../../layers/assignUniqueLayerCode.ts';
import { type IMapGroup } from '../../../layers/MapGroup.ts';
import { type IMapLayer } from '../../../layers/MapLayer.ts';
import { type ILayerCatalogLevels } from './parseLayerCatalogLevels.ts';

interface IImportedLayersResult {
  importedLayers: IMapLayer[];
  skippedCadastral: number;
}

/**
 * Rende univoci i `code` dei layer importati rispetto a quelli gia'
 * presenti nel widget (e tra loro), appendendo un suffisso sequenziale.
 * Il layer con preset catastale viene importato solo se non ne esiste
 * gia' uno: ce ne puo' essere al massimo uno per widget.
 */
function buildImportedLayers(
  currentLayers: IMapLayer[],
  incomingLayers: IMapLayer[],
): IImportedLayersResult {
  const usedCodes = new Set(
    currentLayers.map(layer => layer.code).filter(Boolean),
  );
  let hasCadastralPreset = currentLayers.some(
    layer => layer.intersection?.cadastralPreset,
  );
  let skippedCadastral = 0;
  const importedLayers: IMapLayer[] = [];

  for (const layer of incomingLayers) {
    if (layer.intersection?.cadastralPreset) {
      if (hasCadastralPreset) {
        skippedCadastral++;
        continue;
      }
      hasCadastralPreset = true;
    }
    importedLayers.push(assignUniqueLayerCode(layer, usedCodes));
  }

  return { importedLayers, skippedCadastral };
}

interface IMergeSummary {
  addedLayers: number;
  addedGroups: number;
  skippedGroups: number;
  skippedCadastral: number;
}

/**
 * Mostra un messaggio di riepilogo con il conteggio delle voci aggiunte
 * e di quelle ignorate durante l'importazione dal catalogo.
 */
function notifyMergeSummary({
  addedLayers,
  addedGroups,
  skippedGroups,
  skippedCadastral,
}: IMergeSummary) {
  const parts = [
    addedLayers > 0 &&
      t('map-catalog.merge.layers-added', '{{count}} livelli aggiunti', {
        count: addedLayers,
      }),
    addedGroups > 0 &&
      t('map-catalog.merge.groups-added', '{{count}} gruppi aggiunti', {
        count: addedGroups,
      }),
    skippedGroups > 0 &&
      t(
        'map-catalog.merge.groups-skipped',
        '{{count}} gruppi duplicati ignorati',
        {
          count: skippedGroups,
        },
      ),
    skippedCadastral > 0 &&
      t(
        'map-catalog.merge.cadastral-skipped',
        '{{count}} preset catastali ignorati (ne è ammesso uno solo)',
        {
          count: skippedCadastral,
        },
      ),
  ].filter(Boolean);

  if (parts.length === 0) {
    message.info(
      t('map-catalog.merge.no-entries', 'Nessuna voce da importare'),
    );
  } else if (addedLayers > 0 || addedGroups > 0) {
    message.success(parts.join(', '));
  } else {
    message.info(parts.join(', '));
  }
}

/**
 * Aggiunge livelli e gruppi importati dal catalogo a quelli esistenti.
 * I livelli vengono sempre accodati; i loro `code` vengono resi univoci
 * rispetto a quelli già presenti (suffisso sequenziale) per non violare
 * l'unicità dei codici layer.
 * I gruppi con nome già presente vengono ignorati per evitare duplicati,
 * poiché il widget mappa li identifica per nome (`IMapLayer.group`).
 * Mostra un messaggio di riepilogo con il conteggio di voci aggiunte.
 */
export function mergeLayerCatalogEntries(
  layersEditor: RelationEditor<IMapLayer>,
  groupEditor: RelationEditor<IMapGroup>,
  levels: ILayerCatalogLevels,
) {
  const currentLayers = layersEditor.getValue() ?? [];
  const currentGroups = groupEditor.getValue() ?? [];

  // I gruppi vengono deduplicati per nome: il widget mappa assume nomi univoci
  const newGroups = differenceBy(levels.groups, currentGroups, 'name');

  const { importedLayers, skippedCadastral } = buildImportedLayers(
    currentLayers,
    levels.layers,
  );

  if (importedLayers.length > 0) {
    layersEditor.replace([...currentLayers, ...importedLayers]);
  }
  if (newGroups.length > 0) {
    groupEditor.replace([...currentGroups, ...newGroups]);
  }

  notifyMergeSummary({
    addedLayers: importedLayers.length,
    addedGroups: newGroups.length,
    skippedGroups: levels.groups.length - newGroups.length,
    skippedCadastral,
  });
}
